import { useId, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  ColumnDef,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import dayjs from "dayjs";
import { Filter, Plus } from "lucide-react";
import { useRecurringInvoices } from "@/hooks/invoice/use-recurring-invoices";
import { RecurringInvoiceSchedule } from "@/queries/invoice/types";
import { recurringIntervalOptions } from "@/constants/invoice";
import { RecurringStatusBadge } from "./recurring-status-badge";
import { RecurringActionsMenu } from "./recurring-actions-menu";
import MainTable from "@/components/main-table";
import TablePagination from "@/components/table-pagination";
import SearchInput from "@/components/search-input";
import TableColumnsManager from "@/components/table-columns-manager";
import { Button } from "@/components/ui/button-tw";
import { Checkbox } from "@/components/ui/check-box";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { addresseeNamePriority } from "@/utils/invoice";

/**
 * Build a fake invoice-shaped object from the recurring template so we can
 * reuse the shared `addresseeNamePriority` helper that the regular invoice
 * table uses (priority: DBA → business_name → full name).
 */
const getContactName = (template: Record<string, unknown>): string => {
  const name = addresseeNamePriority({
    invoice_info: { invoice_to: template.invoice_to },
  });
  return name || "—";
};

const getIntervalLabel = (days: number): string => {
  const option = recurringIntervalOptions.find((o) => o.value === days);
  return option?.label ?? `Every ${days} days`;
};

const formatCurrency = (amount: unknown): string => {
  const num = typeof amount === "number" ? amount : 0;
  return `$${num.toFixed(2)}`;
};

const multiColumnFilterFn = (
  row: { original: RecurringInvoiceSchedule },
  _columnId: string,
  filterValue: string
) => {
  const to = row.original.template?.invoice_to as
    | Record<string, string>
    | undefined;
  const searchable = `${to?.first_name || ""} ${to?.last_name || ""} ${
    to?.email || ""
  } ${to?.business_name || ""} ${to?.business_dba || ""}`.toLowerCase();
  return searchable.includes((filterValue ?? "").toLowerCase());
};

const statusFilterFn = (
  row: { getValue: (id: string) => unknown },
  columnId: string,
  filterValue: string[]
) => {
  if (!filterValue?.length) return true;
  return filterValue.includes(row.getValue(columnId) as string);
};

const columns: ColumnDef<RecurringInvoiceSchedule>[] = [
  {
    id: "contact",
    header: "Contact",
    accessorFn: (row) => getContactName(row.template),
    cell: ({ row }) => (
      <span className="font-medium">
        {getContactName(row.original.template)}
      </span>
    ),
    filterFn: multiColumnFilterFn,
    size: 200,
  },
  {
    id: "interval",
    header: "Interval",
    accessorFn: (row) => row.interval_days,
    cell: ({ row }) => getIntervalLabel(row.original.interval_days),
    size: 140,
  },
  {
    id: "status",
    header: "Status",
    accessorFn: (row) => row.status,
    cell: ({ row }) => <RecurringStatusBadge status={row.original.status} />,
    filterFn: statusFilterFn,
    size: 120,
  },
  {
    id: "nextSendDate",
    header: "Next Send Date",
    accessorFn: (row) => row.next_send_date,
    cell: ({ row }) =>
      row.original.status === "cancelled"
        ? "—"
        : dayjs(row.original.next_send_date).format("MMM D, YYYY"),
    size: 150,
  },
  {
    id: "seriesCount",
    header: "Sent",
    accessorFn: (row) => row.series_count,
    cell: ({ row }) => row.original.series_count,
    size: 80,
  },
  {
    id: "amount",
    header: "Amount",
    accessorFn: (row) =>
      (row.template as Record<string, unknown>)?.invoice_total,
    cell: ({ row }) =>
      formatCurrency(
        (row.original.template as Record<string, unknown>)?.invoice_total
      ),
    size: 120,
  },
  {
    id: "actions",
    header: "",
    cell: ({ row }) => <RecurringActionsMenu schedule={row.original} />,
    size: 60,
    enableSorting: false,
  },
];

export function RecurringInvoicesTable() {
  const id = useId();
  const history = useHistory();
  const { data: schedules, isLoading } = useRecurringInvoices();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const tableData = useMemo(() => schedules ?? [], [schedules]);

  // Derive unique status values + counts
  const uniqueStatusValues = useMemo(() => {
    const counts = new Map<string, number>();
    tableData.forEach((s) => {
      counts.set(s.status, (counts.get(s.status) || 0) + 1);
    });
    return { values: Array.from(counts.keys()), counts };
  }, [tableData]);

  const selectedStatuses = useMemo(
    () =>
      (columnFilters.find((f) => f.id === "status")?.value as string[]) ?? [],
    [columnFilters]
  );

  const handleStatusChange = (checked: boolean | "indeterminate", value: string) => {
    const next = checked
      ? [...selectedStatuses, value]
      : selectedStatuses.filter((v) => v !== value);
    if (next.length === 0) {
      setColumnFilters((prev) => prev.filter((f) => f.id !== "status"));
    } else {
      setColumnFilters((prev) => {
        const rest = prev.filter((f) => f.id !== "status");
        return [...rest, { id: "status", value: next }];
      });
    }
  };

  const table = useReactTable({
    data: tableData,
    columns,
    state: { sorting, globalFilter, columnFilters },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    globalFilterFn: multiColumnFilterFn,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap xl:flex-nowrap items-center justify-between gap-3 flex-col-reverse lg:flex-row">
        {/* Left: search + filters + view */}
        <div className="flex items-center gap-3 flex-col lg:flex-row">
          <SearchInput
            searchValue={globalFilter}
            setSearchValue={setGlobalFilter}
          />
          <div className="flex items-center gap-3 mr-3">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline">
                  <Filter
                    className="-ms-1 me-2 opacity-60"
                    size={16}
                    strokeWidth={2}
                    aria-hidden="true"
                  />
                  Filters
                  {columnFilters.length > 0 && (
                    <span className="-me-1 ms-3 inline-flex h-5 max-h-full items-center rounded border border-border bg-background px-1 font-[inherit] text-[0.625rem] font-medium text-muted-foreground/70">
                      {columnFilters.length}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="min-w-[340px] p-3 py-0" align="start">
                <div className="flex justify-between items-center my-2">
                  <div className="text-normal font-medium text-slate-800">
                    Filters
                  </div>
                  <Button
                    variant="link"
                    onClick={() => setColumnFilters([])}
                  >
                    Clear all
                  </Button>
                </div>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="status" className="py-2 border-t">
                    <AccordionTrigger className="py-2 hover:no-underline text-sm font-medium text-muted-foreground">
                      Status
                    </AccordionTrigger>
                    <AccordionContent className="py-2 text-muted-foreground">
                      <div className="space-y-3">
                        {uniqueStatusValues.values.map((value, i) => (
                          <div key={value} className="flex items-center gap-2">
                            <Checkbox
                              id={`${id}-status-${i}`}
                              checked={selectedStatuses.includes(value)}
                              onCheckedChange={(checked) =>
                                handleStatusChange(checked, value)
                              }
                            />
                            <Label
                              htmlFor={`${id}-status-${i}`}
                              className="flex grow justify-between gap-2 font-normal capitalize"
                            >
                              {value}
                              <span className="ms-2 text-xs text-muted-foreground">
                                {uniqueStatusValues.counts.get(value)}
                              </span>
                            </Label>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </PopoverContent>
            </Popover>
            <TableColumnsManager table={table} />
          </div>
        </div>

        {/* Right: create button */}
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto items-center">
          <Button
            className="bg-green-600 hover:bg-green-600/80 text-white"
            onClick={() => history.push("/invoices/new")}
          >
            <Plus
              className="-ms-1 me-2"
              size={16}
              strokeWidth={2}
              aria-hidden="true"
            />
            Create Invoice
          </Button>
        </div>
      </div>
      <MainTable table={table} isLoading={isLoading} />
      <TablePagination table={table} />
    </div>
  );
}
